import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import { Card, Layout } from 'tea-component';
import { useHistory, useParams } from 'umi';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import RangePicker, {
  IRangePickerRef,
} from '@/pages/home/components/RangePicker';
import '@/pages/home/components/RangePicker/index.scss';
import '@intelligence/v3/styles/rangepicker-footer.scss';
import StoreRankDetailsCore from '@intelligence/pages/TopCharts/components/StoreRankDetailsCore';
import useStoreRankDetailsCoreUI from '@intelligence/pages/TopCharts/components/StoreRankDetailsCore/hooks/useStoreRankDetailsCoreUI';
import CascadeRanking from '@intelligence/components/CascadeRanking';

import { MINI_GAME_ENTITY_TYPE } from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import ExportTable from '@intelligence/components/ExportTable';
import useInitialDateRange from '@intelligence/v3/_hooks/useInitialDateRange';
import BasicInfoHeader from '@intelligence/v3/components/BasicInfoHeader';
import { getLocalizedValue } from '@intelligence/v3/components/BasicInfoHeader/utils';
import type { BasicInfoMetaItem } from '@intelligence/v3/components/BasicInfoHeader/types';
import PublisherWithPopover from './components/PublisherWithPopover';
import useMiniGameDetailsData from './hooks/useMiniGameDetailsData';
import {
  getDefaultDateRange,
  getBasicInfoProducts,
  getPlatformIcon,
  formatMiniGameDimensionName,
} from './utils';
import {
  DATE_FORMAT,
  DATE_TYPE,
  DATE_TYPES,
  DEFAULT_DATE_SHORTCUT,
  DEFAULT_MINIGAME_TYPES,
  MINI_GAME_CASCADE_OPTIONS,
  MINI_GAME_EXPORT_CONFIG,
} from './config';
import type { IMiniGameDetailsProps, IGameDetailDateRange } from './types';
import './index.scss';

const { Content } = Layout;

const MiniGameDetails: React.FC<IMiniGameDetailsProps> = ({ match }) => {
  const { formatMessage, isEN } = useIntlExtend();
  const history = useHistory();
  const routeParams = useParams<{ id?: string; }>();
  const gameId = match?.params?.id || routeParams?.id || '';
  const initialRange = useInitialDateRange();
  const defaultRange = useMemo(
    () => getDefaultDateRange(initialRange),
    [initialRange],
  );

  // 已确认的日期范围（驱动数据请求）
  const [dateRange, setDateRange] = useState<IGameDetailDateRange>(defaultRange);
  // 草稿日期范围（弹窗内编辑中，未确认）
  const [draftRange, setDraftRange] = useState<IGameDetailDateRange>(defaultRange);
  // 用于 Reset 时强制同步日历与快捷键状态
  const [rangePickerKey, setRangePickerKey] = useState(0);
  const rangePickerRef = useRef<IRangePickerRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [minigameTypes, setMinigameTypes] = useState<string[]>(DEFAULT_MINIGAME_TYPES);

  const formatDimensionName = useCallback(
    (dimension: string) => formatMiniGameDimensionName(dimension, formatMessage),
    [formatMessage],
  );

  const {
    basicInfo,
    basicInfoLoading,
    metricsData,
    metricsLoading,
    metricsRequestParams,
  } = useMiniGameDetailsData({
    gameId,
    dateRange,
    minigameTypes,
  });

  const visibleDimensions = useMemo<string[]>(() => {
    // 按原始维度顺序过滤可见项
    return DEFAULT_MINIGAME_TYPES.filter(key => minigameTypes.includes(key));
  }, [minigameTypes]);

  const { visualisOption, tablesOption } = useStoreRankDetailsCoreUI({
    isLoading: metricsLoading,
    isSectionalRank: false,
    isExpandable: false,
    data: metricsData,
    visualisHelpperOptionConfig: {
      tooltipHeaderLeftTitle: formatMessage({ id: 'dbi_rank' }),
      rankDimensionsNameFormatter: formatDimensionName,
    },
    tablesHelpperOptionConfig: {
      rankDimensionsNameFormatter: formatDimensionName,
    },
  });

  const filteredVisualisOption = useMemo(
    () => ({
      ...visualisOption,
      sourceDataDimensions: visibleDimensions,
      dimensionIconFormatter: getPlatformIcon,
    }),
    [visualisOption, visibleDimensions, getPlatformIcon],
  );

  const filteredTablesOption = useMemo(
    () => ({
      ...tablesOption,
      showSearch: false,
      sourceDataDimensions: visibleDimensions,
      platformIcon: getPlatformIcon,
    }),
    [tablesOption, visibleDimensions, getPlatformIcon],
  );

  const rangePickerValue = useMemo<[Moment, Moment]>(
    () => [moment(draftRange.startDate), moment(draftRange.endDate)],
    [draftRange],
  );

  const handleRangeChange = useCallback(
    (value: Array<Moment | '' | null>) => {
      const [start, end] = value || [];
      if (
        !moment.isMoment(start) ||
        !start?.isValid() ||
        !moment.isMoment(end) ||
        !end?.isValid()
      )
        return;
      setDraftRange({
        startDate: start.format(DATE_FORMAT),
        endDate: end.format(DATE_FORMAT),
      });
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    setDateRange(draftRange);
    // 失焦输入框以关闭快捷浮层
    const inputs = containerRef.current?.querySelectorAll('input');
    inputs?.forEach((input) => input.blur());

    rangePickerRef.current?.close();
  }, [draftRange]);

  const handleReset = useCallback(() => {
    setDraftRange(defaultRange);
    setRangePickerKey((prev) => prev + 1);
  }, [defaultRange]);

  const isLoading = metricsLoading || basicInfoLoading;

  const basicInfoData = useMemo(() => {
    const products = getBasicInfoProducts(basicInfo);
    const name =
      getLocalizedValue(basicInfo, 'app_name', isEN) || basicInfo?.name || '-';
    const cover =
      basicInfo?.app_cover || basicInfo?.cover || basicInfo?.icon || '';
    const wechatPublisher = getLocalizedValue(basicInfo, 'wechat_publisher', isEN);
    const douyinPublisher = getLocalizedValue(basicInfo, 'douyin_publisher', isEN);
    const publisher =
      getLocalizedValue(basicInfo, 'publisher', isEN) ||
      getLocalizedValue(basicInfo, 'developer', isEN);
    const mainGenre =
      getLocalizedValue(basicInfo, 'minigame_genre', isEN) ||
      getLocalizedValue(basicInfo, 'genre', isEN);
    const subGenre =
      getLocalizedValue(basicInfo, 'minigame_sub_genre', isEN) ||
      getLocalizedValue(basicInfo, 'sub_genre', isEN);

    /** 是否有任意发行商信息（微信/抖音/通用兜底） */
    const hasAnyPublisher = Boolean(wechatPublisher || douyinPublisher || publisher);

    const metaItems: BasicInfoMetaItem[] = [
      hasAnyPublisher && {
        key: 'publisher',
        value: (
          <PublisherWithPopover
            wechatPublisher={wechatPublisher}
            douyinPublisher={douyinPublisher}
            publisher={publisher}
            products={products}
          />
        ),
      },
    ].filter(Boolean) as BasicInfoMetaItem[];

    // 品类信息展示在 extra 区域
    const genreExtra = (
      <>
        {mainGenre && (
          <span className="dbi-basic-info-header__action-tab" key="mainGenre">
            <span className="dbi-basic-info-header__action-tab-title">
              {formatMessage({ id: 'dbi_main_genre' })}
            </span>
            <span className="dbi-basic-info-header__action-tab-context">{mainGenre}</span>
          </span>
        )}
        {subGenre && (
          <span className="dbi-basic-info-header__action-tab" key="subGenre">
            <span className="dbi-basic-info-header__action-tab-title">
              {formatMessage({ id: 'dbi_sub_genre' })}
            </span>
            <span className="dbi-basic-info-header__action-tab-context">{subGenre}</span>
          </span>
        )}
      </>
    );

    return { name, cover, metaItems, extra: genreExtra, backgroundImage: basicInfo?.image || '' };
  }, [basicInfo, formatMessage, isEN]);

  return (
    <Content className="dbi-mini-game-details">
      <Content.Header
        title={formatMessage({ id: 'dbi_unified_game_details' })}
        showBackButton
        onBackButtonClick={() => history.goBack()}
      >
        <BasicInfoHeader
          isLoading={basicInfoLoading}
          cover={basicInfoData.cover}
          name={basicInfoData.name}
          backgroundImage={basicInfoData.backgroundImage}
          metaItems={basicInfoData.metaItems}
          extra={basicInfoData.extra}
        />
      </Content.Header>
      <Content.Body full>
        <Card className="dbi-mini-game-details__filter-card">
          <div className="dbi-mini-game-details__filters">
            <div className="search-item-date" ref={containerRef}>
              <RangePicker
                key={rangePickerKey}
                ref={rangePickerRef}
                className="search-border"
                overlayClassName="dbi-mini-game-details-rangepicker-overlay"
                type={DATE_TYPE}
                typeList={DATE_TYPES}
                showSelectType={false}
                showShortcut={true}
                showShortcutTypeTitle={false}
                language={isEN ? 'en' : 'cn'}
                value={rangePickerValue}
                onChange={handleRangeChange}
                defualtSelectedShortcut={DEFAULT_DATE_SHORTCUT}
                autoResetShortcutByValueChange={false}
                syncInput={false}
                changeClose={false}
                footerConfig={{
                  className: 'dtb-rangepicker-footer',
                  right: (
                    <>
                      <button
                        type="button"
                        className="dtb-rangepicker-footer-reset"
                        onClick={handleReset}
                      >
                        {formatMessage({ id: 'dbi_reset' })}
                      </button>
                      <button
                        type="button"
                        className="dtb-rangepicker-footer-confirm"
                        onClick={handleConfirm}
                      >
                        {formatMessage({ id: 'dbi_confirm' })}
                      </button>
                    </>
                  ),
                }}
              />
            </div>
            <CascadeRanking
              value={minigameTypes}
              onChange={setMinigameTypes}
              options={MINI_GAME_CASCADE_OPTIONS}
            />
          </div>
        </Card>
        <StoreRankDetailsCore
          entityType={MINI_GAME_ENTITY_TYPE}
          gameId={gameId}
          title={formatMessage({ id: 'dbi_rank_trend' })}
          countries={[]}
          isLoading={isLoading}
          visualisOption={filteredVisualisOption}
          tablesOption={filteredTablesOption}
          selectedMode="multiple"
          isNewOpenTable={false}
          gameName={basicInfoData.name}
          gameCover={basicInfoData.cover}
          dateType="daily"
          startDate={metricsRequestParams.start_date}
          endDate={metricsRequestParams.end_date}
          downloadBtn={
            <ExportTable
              entityType={MINI_GAME_ENTITY_TYPE}
              businessType={MINI_GAME_EXPORT_CONFIG.businessType}
              isDisableDownload={isLoading}
              isDisableDataAmount
              downloadModalSize={isEN ? 684 : 580}
              total={tablesOption?.sourceData?.length ?? 0}
              tableParams={{
                ...metricsRequestParams,
                minigame_types: visibleDimensions,
              }}
              newStyleFileName={`${basicInfoData.name}_Store Rank`}
              useWebDownload={true}
              downloadKey={MINI_GAME_EXPORT_CONFIG.downloadKey}
            />
          }
        />
      </Content.Body>
    </Content>
  );
};

export default MiniGameDetails;
